// Procedural sound effects (ported from Hexagon). No audio files: everything is
// synthesized with Web Audio, which also keeps the game working offline.

const MUTE_KEY = 'cyp.muted';

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (isMuted()) return null;
  try {
    ctx ??= new AudioContext();
    // Browsers start the context suspended until a user gesture; any click resumes it.
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // Not persisted; fine.
  }
}

function tone(freq: number, start: number, duration: number, type: OscillatorType = 'sine', gain = 0.15): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime + start;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  osc.connect(g).connect(a.destination);
  osc.start(t);
  osc.stop(t + duration + 0.02);
}

/** A burst of decaying noise: a card landing on the felt. */
function snap(start: number, gain = 0.2, length = 0.04): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime + start;
  const len = Math.floor(a.sampleRate * length);
  const buf = a.createBuffer(1, len, a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 4;
  const src = a.createBufferSource();
  const filter = a.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 1200;
  const g = a.createGain();
  g.gain.value = gain;
  src.buffer = buf;
  src.connect(filter).connect(g).connect(a.destination);
  src.start(t);
}

export const sounds = {
  play(): void {
    snap(0);
  },
  /** A riffle: two quick bursts of soft clicks, then the deal's snaps. */
  shuffle(): void {
    for (const start of [0, 0.32]) for (let i = 0; i < 14; i++) snap(start + i * 0.014, 0.07, 0.012);
    for (let i = 0; i < 6; i++) snap(0.75 + i * 0.11, 0.12, 0.03);
  },
  /** `ours`: the listener's side took it. */
  trickWon(ours: boolean): void {
    if (ours) {
      tone(523, 0, 0.12, 'triangle');
      tone(784, 0.08, 0.18, 'triangle');
    } else {
      tone(392, 0, 0.12, 'triangle', 0.1);
      tone(330, 0.08, 0.16, 'triangle', 0.1);
    }
  },
  yourTurn(): void {
    tone(660, 0, 0.18);
    tone(880, 0.14, 0.28);
  },
  partner(): void {
    tone(440, 0, 0.12, 'square', 0.05);
    tone(660, 0.1, 0.12, 'square', 0.05);
    tone(880, 0.2, 0.2, 'square', 0.05);
  },
  made(): void {
    [523, 659, 784].forEach((f, i) => tone(f, i * 0.1, 0.25, 'triangle'));
  },
  set(): void {
    [392, 330, 262].forEach((f, i) => tone(f, i * 0.14, 0.3, 'sawtooth', 0.06));
  },
  win(): void {
    [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.12, 0.3, 'triangle'));
  },
};
